import { faCheck, faShare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Location } from "history";
import truncate from "lodash-es/truncate";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { contestsHomeUrl } from "@/mk2/apps/contests/urls";
import { HELP_HASH_TARGETS } from "@/mk2/apps/faq/containers/HelpRedirect/HelpRedirect.constants";
import ReplyButton from "@/mk2/apps/forum/components/ReplyButton";
import { TESTINGS_TOPIC_ID } from "@/mk2/apps/forum/constants";
import PostMenu from "@/mk2/apps/forum/containers/PostMenu/PostMenu";
import { ContestEntity, PostEntity } from "@/mk2/apps/forum/schemas";
import {
    forumPostDetailUrl,
    forumPostEditUrl,
    forumUntrustworthyUserFeedUrl,
} from "@/mk2/apps/forum/urls";
import { isCounsellingPost, largestAvailablePhotoVersionOfPost } from "@/mk2/apps/forum/utils";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import AnnouncementBox, { BoxType } from "@/mk2/components/AnnouncementBox";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import Badge, { BadgeType } from "@/mk2/components/Badge";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { DeletedPostAlert } from "@/mk2/components/DeletedPostAlert";
import { Emojify } from "@/mk2/components/Emojify";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { Img } from "@/mk2/components/Img";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import Sticker, { StickerSize } from "@/mk2/components/Sticker";
import { UntrustworthyUserAlert } from "@/mk2/components/UntrustworthyUserAlert";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import {
    getAllDescendantsOfAType,
    getAllPlaintext,
    getAttributesDict,
    removeQuotes,
} from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { timeAt } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { PhotoEntity } from "@/mk2/schemas";
import {
    CONTEST_IS_FINISHED_EXPLANATION1,
    CONTEST_IS_FINISHED_EXPLANATION2_link,
    COUNSELLING_ANSWERED,
    FORUM_POST_WITH_AUTO_TITLE_IS_EDITABLE__FAQ,
    FORUM_POST_WITH_AUTO_TITLE_faq_IS_EDITABLE_here,
    HERE,
    POST_REMOVE_COMPLAINTS,
    RUNNING_CONTESTS,
    SHARE,
    TOPIC_MODERATOR,
} from "@/mk/autogenerated/translation";
import { upperCaseFirst } from "@/mk/bazaar/common/filters";
import { authorIsModerator, isAnonymousUser } from "@/mk/bazaar/common/userUtils";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./Post.mscss";
import "./Post.scss";

interface OwnProps {
    post: PostEntity;
    isMyAnonymousPost?: boolean;
    isAuthor: boolean;
    isAdmin?: boolean;
    isTitleEditableOnceAgain: boolean;
    contest: ContestEntity;
    isMobile: boolean;
    style?: any;
    className?: string;
    role?: "author" | "moderator" | "admin";
    followLinks: boolean;
    location: Location;
    hasReply: boolean;
    bannerAd?: React.ReactNode;

    onReply(username: string, anonUserHash?: string);
}

interface State {
    galleryIndex: number;
}

export default class Post extends React.Component<OwnProps, State> {
    public state: State = {
        galleryIndex: 0,
    };

    private astToHtmlCache = cacheLast(true);

    public shouldComponentUpdate(n: OwnProps) {
        const p = this.props;

        return (
            p.post.id !== n.post.id ||
            p.post.perexAST !== n.post.perexAST ||
            p.post.comments !== n.post.comments ||
            /* Homepage has cached likeable as weak ref so likeable is not loaded yet */
            (p.post.likeable && p.post.likeable.likesCount) !==
                (n.post.likeable && n.post.likeable.likesCount) ||
            (p.post.likeable && p.post.likeable.isLiked) !==
                (n.post.likeable && n.post.likeable.isLiked) ||
            p.post.stickerId !== n.post.stickerId ||
            p.post.deletedAt !== n.post.deletedAt ||
            p.bannerAd !== n.bannerAd ||
            p.post.sticky !== n.post.sticky ||
            (p.contest === null && n.contest !== null) // Contest is loaded
        );
    }

    public render() {
        const {
            className,
            contest,
            followLinks,
            hasReply,
            isMyAnonymousPost,
            location,
            post,
            role,
            style,
            bannerAd,
            isAuthor,
            isTitleEditableOnceAgain,
            isMobile,
        } = this.props;

        const authorIsTopicModerator = authorIsModerator(
            post.author,
            post.topic.topicModerators?.map((tm) => tm.user) || post.topic.moderators,
        );

        // prepare post message body
        let bodyChunks: React.ReactChild[] = null;
        const inlinedImageNumbers = [];
        let belowImages = [];

        if (post.perexAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
                renderSpecialWikiLinks: false,
                renderLinksWithNofollow: !followLinks,
                renderLinksOfUntrustworthy: post.author.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                    : false,
                linkedObjects: post.perexASTAdditionalData
                    ? post.perexASTAdditionalData.linkedObjects
                    : {},
                photos: post.messagePhotos?.map((mp) => mp.photofile) || post.photos,
                customStyles: {
                    photo: styles.Post__body__responsivePhoto,
                    paragraph: styles.Post__body__paragraph,
                    instagram: styles.Post__body__instagram,
                    tiktok: styles.Post__body__instagram,
                },
                rootDiv: false,
                selectPhotoVersionFn: (photo) =>
                    largestAvailablePhotoVersionOfPost(photo, contest ? "s1600x1600" : "s600x600"),
                hashizeFn: (bodyChunk) => bodyChunk,
            };
            bodyChunks = this.astToHtmlCache(
                () => astToHtml(post.perexAST, additionalData),
                post.perexAST,
            );

            const picNodes = getAllDescendantsOfAType(post.perexAST, "pic");
            for (const picNode of picNodes) {
                const attributes = getAttributesDict(picNode);
                const picCd = removeQuotes(
                    attributes.hasOwnProperty("code")
                        ? attributes.code
                        : attributes.hasOwnProperty("cd")
                          ? attributes.cd
                          : "",
                );
                if (picCd !== "") {
                    const inlinedImageNumber = (
                        post.messagePhotos?.map((mp) => mp.photofile) || post.photos
                    ).findIndex((value: PhotoEntity) => {
                        return value.code === picCd;
                    });
                    if (inlinedImageNumber !== -1) {
                        inlinedImageNumbers.push(inlinedImageNumber);
                    }
                }
            }
            belowImages = (post.messagePhotos?.map((mp) => mp.photofile) || post.photos).filter(
                (photo, idx) => !inlinedImageNumbers.includes(idx),
            );
        }

        const showReply = hasReply && !post.deletedAt && !post.topic.deletedAt;
        const isCounselling = isCounsellingPost(post);
        const isAnsweredCounselling = isCounselling && Boolean(post.comments);

        const showLike = !post.deletedAt && !post.topic.deletedAt && !isCounselling;
        const showShare = !post.deletedAt && !post.topic.deletedAt && !isCounselling;

        const isTesting = post.topic.id === TESTINGS_TOPIC_ID;
        const showEditTitle =
            (isAuthor || isMyAnonymousPost) &&
            !post.deletedAt &&
            !isTesting &&
            isTitleEditableOnceAgain;
        const postEditUrl = url(forumPostEditUrl, { postId: post.id });
        return (
            <div
                className={cx(styles.Post, className, post.deletedAt && styles["Post--deleted"])}
                style={style}
            >
                <div className={styles.Post__body}>
                    <div className={styles.Post__body__headingline}>
                        <h1
                            className={cx(
                                styles.Post__body__heading,
                                !bodyChunks && styles.Post__body__no_perex,
                            )}
                        >
                            <Emojify>{upperCaseFirst(post.heading)}</Emojify>
                        </h1>
                        {showEditTitle && (
                            <div className={styles.Post__body__editableAutoTitle}>
                                <Interpolate
                                    i18nKey={FORUM_POST_WITH_AUTO_TITLE_faq_IS_EDITABLE_here}
                                    faq={
                                        <Link to={HELP_HASH_TARGETS["why-we-rename-forum-post"]}>
                                            {FORUM_POST_WITH_AUTO_TITLE_IS_EDITABLE__FAQ}
                                        </Link>
                                    }
                                    here={<Link to={postEditUrl}>{HERE}</Link>}
                                />
                            </div>
                        )}
                        {isAnsweredCounselling && (
                            <span className={styles["Post__body__headingbadge-answered"]}>
                                <span className={styles["Post__body__headingbadge-answered__icon"]}>
                                    <FontAwesomeIcon icon={faCheck} />
                                </span>
                                {COUNSELLING_ANSWERED}
                            </span>
                        )}
                    </div>
                    <div className={styles.Post__body__container}>
                        <div className={styles.Post__body__meta}>
                            {!contest && !isCounselling && (
                                <div className={styles.Post__body__meta__block}>
                                    {!isAnonymousUser(post.author) && (
                                        <Link
                                            to={url(blogsProfileUrl, {
                                                username: post.author.username,
                                            })}
                                        >
                                            <AvatarImg
                                                size={24}
                                                user={post.author}
                                                className={styles.Post__body__meta__avatar}
                                            />
                                            <span className={styles.Post__body__meta__author}>
                                                {post.author.username}
                                            </span>
                                        </Link>
                                    )}
                                    {!!post.author.isAmbassadorOfBrandId && (
                                        <Badge
                                            type={
                                                post.author.isCommercialAmbassador
                                                    ? BadgeType.CommercialAmbassador
                                                    : BadgeType.Ambassador
                                            }
                                            className={styles.Post__badge}
                                        />
                                    )}
                                    {!!post.author.isOfficialProfile && (
                                        <Badge
                                            type={BadgeType.OfficialProfile}
                                            className={styles.Post__badge}
                                        />
                                    )}
                                    {authorIsTopicModerator && (
                                        <span className={styles.Post__body__meta__title}>
                                            {TOPIC_MODERATOR}
                                        </span>
                                    )}
                                    <div
                                        className={cx(
                                            styles.Post__body__meta__created,
                                            !isAnonymousUser(post.author) &&
                                                styles.Post__body__meta__sep,
                                        )}
                                    >
                                        {timeAt(post.created)}
                                    </div>
                                </div>
                            )}

                            {(contest || isCounselling) && (
                                <div className={styles.Post__body__meta__created}>
                                    {timeAt(post.created)}
                                </div>
                            )}
                        </div>

                        <PostMenu
                            className={styles.Post__menu}
                            post={post}
                            isMyAnonymousPost={isMyAnonymousPost}
                            isTitleEditableOnceAgain={isTitleEditableOnceAgain}
                            contest={contest}
                            location={location}
                        />

                        {bannerAd}

                        {contest && !contest.isDraft && !contest.isActive && (
                            <div>
                                <AnnouncementBox
                                    type={BoxType.Yellow}
                                    hasDetailMessage
                                    message={CONTEST_IS_FINISHED_EXPLANATION1}
                                    detailMessage={
                                        <Interpolate
                                            i18nKey={CONTEST_IS_FINISHED_EXPLANATION2_link}
                                            link={
                                                <Link to={contestsHomeUrl}>{RUNNING_CONTESTS}</Link>
                                            }
                                        />
                                    }
                                />
                            </div>
                        )}

                        <div className={styles.Post__body__content}>
                            <div className={styles.Post__body__content__text}>
                                <div>
                                    {post.deletedAt && (
                                        <DeletedPostAlert
                                            deletedByRole={post.deletedByRole}
                                            deletedBy={post.deletedBy?.username}
                                            deletedAt={post.deletedAt}
                                            reason={post.deletedReason}
                                        />
                                    )}

                                    {post.author?.isUntrustworthy && !post.deletedAt ? (
                                        <UntrustworthyUserAlert user={post.author} />
                                    ) : null}

                                    {!!bodyChunks && (
                                        <div className={styles.Post__body__content__text__perex}>
                                            <Emojify size={17}>{bodyChunks}</Emojify>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {post.stickerId && (
                                <div>
                                    <Sticker id={post.stickerId} size={StickerSize.Small} />
                                </div>
                            )}

                            {belowImages.length > 0 && (
                                <div className={styles.Post__body__gallery}>
                                    {belowImages.length === 1 ? (
                                        <a
                                            href={photoUrl(belowImages[0], "s1600x1600")}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Img
                                                lazy
                                                src={photoUrl(
                                                    belowImages[0],
                                                    largestAvailablePhotoVersionOfPost(
                                                        belowImages[0],
                                                        "s600x600",
                                                    ),
                                                )}
                                                height={belowImages[0].height}
                                                width={belowImages[0].width}
                                                className={styles.Post__body__photos}
                                                alt={post.heading}
                                            />
                                        </a>
                                    ) : (
                                        <ImageGalleryLoadable
                                            items={belowImages.map(
                                                (photo): ReactImageGalleryItem => ({
                                                    original: photoUrl(
                                                        photo,
                                                        largestAvailablePhotoVersionOfPost(
                                                            photo,
                                                            "s600x600",
                                                        ),
                                                    ),
                                                    originalClass:
                                                        styles.Post__body__gallery__clickable,
                                                }),
                                            )}
                                            showThumbnails={false}
                                            showPlayButton={false}
                                            showFullscreenButton={false}
                                            showNav={!isMobile}
                                            showBullets
                                            infinite={false}
                                            slideDuration={300}
                                            onClick={this.handleGalleryClick(belowImages)}
                                            onSlide={this.handleGallerySlide}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {(role === "admin" || role === "moderator") && (
                    <Complaints
                        className={styles.Post__complaints}
                        id={post.id}
                        entity={ComplaintTargetType.ForumThread}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                    />
                )}

                {(showReply || showLike) && (
                    <div className={styles.Post__footer}>
                        {showLike && (
                            <Like
                                entity="forum.thread"
                                id={post.id}
                                type={LikeType.IconCount}
                                disabled={isMyAnonymousPost}
                            />
                        )}
                        {showReply && (
                            <ReplyButton
                                onClick={this.handleOnReply}
                                asAnchor={false}
                                link={null}
                            />
                        )}
                        {showShare && (
                            <Btn
                                label={SHARE}
                                icon={faShare}
                                onClick={this.handleOnShare}
                                type={BtnType.GreyText}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    }

    private handleOnReply = () => {
        const { post, onReply } = this.props;
        const username = isAnonymousUser(post.author) ? null : post.author.username;
        const anonUserHash = isAnonymousUser(post.author) ? "autor" : null;
        onReply(username, anonUserHash);
    };

    private handleOnShare = () => {
        const { post } = this.props;
        if (post && navigator.share) {
            const topicSlug = post.topic.slug;
            const postSlug = post.slug;

            navigator
                .share({
                    url: url(forumPostDetailUrl, { topicSlug, postSlug }),
                    title: truncate(getAllPlaintext(post.perexAST), { length: 120 }),
                })
                .then(() => {
                    // alert('Zdieľanie bolo úspešné.');
                })
                .catch((error) => {
                    if (!(error instanceof DOMException && error.name === "AbortError")) {
                        alert(`Chyba pri zdieľaní: ${error}`);
                    }
                });
        } else {
            alert("Zdieľanie nie je na tomto zariadení podporované.");
        }
    };

    private handleGallerySlide = (currentIndex: number) => {
        this.setState({ galleryIndex: currentIndex });
    };

    private handleGalleryClick = (images: PhotoEntity[]) => () => {
        const { galleryIndex } = this.state;
        const photo = images[galleryIndex];
        if (photo) {
            window.open(photoUrl(photo, "s1600x1600"), "_blank");
        }
    };
}
